/** SDR analytics dates as YYYYMMDD (local calendar). */

const VALID_THROUGH = /valid\s+through\s+(.+)/i

export function parseFlexibleDateMs(raw: string | undefined): number | undefined {
  if (!raw) {
    return undefined
  }
  const s = raw.trim()
  if (!s) {
    return undefined
  }
  // Parse ISO calendar dates locally; Date.parse('yyyy-MM-dd') is UTC and can shift the day.
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    const y = parseInt(iso[1], 10)
    const m = parseInt(iso[2], 10)
    const d = parseInt(iso[3], 10)
    const u = new Date(y, m - 1, d).getTime()
    return Number.isNaN(u) ? undefined : u
  }
  const t = Date.parse(s)
  if (!Number.isNaN(t)) {
    return t
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdy) {
    const mm = parseInt(mdy[1], 10)
    const dd = parseInt(mdy[2], 10)
    const yyyy = parseInt(mdy[3], 10)
    const u = new Date(yyyy, mm - 1, dd).getTime()
    return Number.isNaN(u) ? undefined : u
  }
  return undefined
}

export function formatAnalyticsYyyyMmDdFromMs(ms: number): string {
  const d = new Date(ms)
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  const day = d.getDate()
  return `${y}${String(m).padStart(2, '0')}${String(day).padStart(2, '0')}`
}

export function formatAnalyticsYyyyMmDd(raw: string | undefined): string | undefined {
  const ms = parseFlexibleDateMs(raw)
  return ms === undefined ? undefined : formatAnalyticsYyyyMmDdFromMs(ms)
}

export function coerceAnalyticsYyyyMmDd(raw: string | undefined): string | undefined {
  if (!raw?.trim()) {
    return undefined
  }
  const formatted = formatAnalyticsYyyyMmDd(raw)
  if (formatted && /^\d{8}$/.test(formatted)) {
    return formatted
  }
  const digits = raw.replace(/\D/g, '')
  return digits.length >= 8 ? digits.slice(0, 8) : undefined
}

export function formatContractMonthDayWithYearGuess(raw: string | undefined, anchorMs: number): string | undefined {
  if (!raw?.trim()) {
    return undefined
  }
  const u = raw.trim()
  const direct = parseFlexibleDateMs(u)
  if (direct !== undefined) {
    return formatAnalyticsYyyyMmDdFromMs(direct)
  }
  const y0 = new Date(anchorMs).getFullYear()
  for (const delta of [0, 1, -1, 2, -2]) {
    const y = y0 + delta
    const ms = Date.parse(`${u} ${y}`)
    if (!Number.isNaN(ms)) {
      return formatAnalyticsYyyyMmDdFromMs(ms)
    }
  }
  return undefined
}

export function parseValidThroughDatesFromNote(note: string | undefined): number | undefined {
  if (!note) {
    return undefined
  }
  const m = note.match(VALID_THROUGH)
  if (!m?.[1]) {
    return undefined
  }
  return parseFlexibleDateMs(m[1].trim())
}

/** Use-year start: day after (end date − 1 calendar year). */
export function inferUseYearBeginMsFromEndMs(endMs: number): number {
  const d = new Date(endMs)
  d.setFullYear(d.getFullYear() - 1)
  d.setDate(d.getDate() + 1)
  return d.getTime()
}

export function parseAnalyticsYyyyMmDdToMs(compact: string): number | undefined {
  if (!/^\d{8}$/.test(compact)) {
    return undefined
  }
  const y = parseInt(compact.slice(0, 4), 10)
  const m = parseInt(compact.slice(4, 6), 10)
  const d = parseInt(compact.slice(6, 8), 10)
  const ms = new Date(y, m - 1, d).getTime()
  return Number.isNaN(ms) ? undefined : ms
}
